import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Container, Grid, Tooltip, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import ColoredTile from 'components/ColoredTile';
import FooterButton from 'components/FooterButton';
import Jumbotron from 'components/Jumbotron';
import Loader from 'components/Loader';
import MobileGuttersContainer from 'components/MobileGuttersContainer';
import Table from 'components/Table';
import TextButton from 'components/TextButton/TextButton';
import { buildTemplatesStatisticsParams } from 'config/api/statistics/statistics.parsers';
import type { TemplatesDashboardRow } from 'config/api/statistics/statistics.types';
import getFirstSelectedAreaIdFromChips from 'helpers/getFirstSelectedAreaIdFromChips';
import { translatedOrFallback } from 'helpers/safeT/safeT';
import useSyncSubareaFilterWithArea from 'hooks/useSyncSubareaFilterWithArea/useSyncSubareaFilterWithArea';
import action_instances_dashboard_messages from 'messages/action_instances_dashboard_messages';
import general_messages from 'messages/general_messages';
import title_messages from 'messages/title_messages';
import TemplatesPivotInstancesDialog from 'pages/ActionInstancesDashboardPage/_components/TemplatesPivotInstancesDialog';
import useStyles, { resolveKpiColor } from 'pages/ActionInstancesDashboardPage/ActionInstancesDashboardPage.styles';
import formatCompletionRate from 'pages/ActionInstancesDashboardPage/formatCompletionRate';
import useTemplatesDashboardData from 'pages/ActionInstancesDashboardPage/useTemplatesDashboardData';
import { useHelpDialogContext } from 'reactContext/HelpDialogContext/HelpDialogContext';
import { getAssetTypeFilter, getSubareaFilterForInstances } from 'services/_filters/parsers/actionLibraryInstanceFilters';
import { getAreaFilter, getSubareaFilter } from 'services/_filters/parsers/actionTemplateFilters';
import type { AvailableFilters } from 'types/ActionLibrary';
import { TableDataMappingRow } from 'types/Table';

const PAGE_SIZE = 20;
/** Max per OpenAPI for `instances_limit` on templates-dashboard. */
const TEMPLATES_DASHBOARD_INSTANCES_SAMPLE_LIMIT = 25;

type FilterChip = { id: string; name: string | string[] };

type FiltersState = Record<string, FilterChip[]>;

const ActionInstancesDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const classes = useStyles();
  const upMd = useMediaQuery(theme.breakpoints.up('md'));
  const { showHelpDialog } = useHelpDialogContext();

  const [filters, setFilters] = useState<FiltersState>({});
  const [availableFilters, setAvailableFilters] = useState<AvailableFilters | null>(null);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const [drillDown, setDrillDown] = useState<{ templateId: number; column: 'a' | 'k'; templateName: string } | null>(null);

  const filterParams = useMemo(
    () =>
      buildTemplatesStatisticsParams(filters, {
        include_instances: true,
        instances_limit: TEMPLATES_DASHBOARD_INSTANCES_SAMPLE_LIMIT,
      }),
    [filters],
  );

  const { accumulatedRows, dashboardKpi, hasNextPage, isError, isFetching, isLoading, loadNextPage, offset, refetch } =
    useTemplatesDashboardData(filterParams, PAGE_SIZE);

  /** After the first successful dashboard fetch, keep `<Table>` mounted while refiltering so `Filters` drawer state is not lost. */
  const hasHydratedDashboardRef = useRef(false);
  useEffect(() => {
    if (!isError && !isLoading && (accumulatedRows.length > 0 || dashboardKpi != null)) {
      hasHydratedDashboardRef.current = true;
    }
  }, [accumulatedRows.length, dashboardKpi, isError, isLoading]);

  const showDashboardTable =
    Boolean(availableFilters) &&
    (hasHydratedDashboardRef.current || !(isLoading && offset === 0)) &&
    (!isError || accumulatedRows.length > 0);

  useSyncSubareaFilterWithArea(filters.area, availableFilters, setAvailableFilters);

  useEffect(() => {
    const load = async () => {
      try {
        const [areaFilter, assetTypeFilter] = await Promise.all([getAreaFilter(), getAssetTypeFilter()]);

        setAvailableFilters({
          area: { ...areaFilter, isSingleSelect: true },
          subarea: { ...(await getSubareaFilterForInstances()), isSingleSelect: true },
          new_filter: {
            sectionName: ['here will be new filter (not work yet)'],
            filters: [
              { id: '1', name: 'a=0' },
              { id: '2', name: 'T>A' },
              { id: '3', name: 'K<A' },
            ],
            isSingleSelect: false,
          },
          asset_type: { ...assetTypeFilter, isSingleSelect: false },
        } as unknown as AvailableFilters);

        const areaId = getFirstSelectedAreaIdFromChips(filtersRef.current.area);
        if (areaId) {
          const subData = await getSubareaFilter(areaId);
          setAvailableFilters(prev =>
            prev ? ({ ...prev, subarea: { ...subData, isSingleSelect: true } } as unknown as AvailableFilters) : prev,
          );
        }
      } catch {
        setAvailableFilters(null);
      }
    };
    load().catch(() => {});
  }, []);

  const openDrillDown = useCallback((templateId: number, column: 'a' | 'k', templateName: string) => {
    setDrillDown({ templateId, column, templateName });
  }, []);

  const closeDrillDown = useCallback(() => setDrillDown(null), []);

  const dataMapping: TableDataMappingRow<TemplatesDashboardRow>[] = useMemo(
    () => [
      {
        id: 'template_name',
        width: '24%',
        label: t(action_instances_dashboard_messages.column_template),
        get: row => {
          const hoverTitle = (
            <div>
              <Typography variant='subtitle2'>{t(action_instances_dashboard_messages.hover_assets_title)}</Typography>
              <ul className={classes.hoverList}>
                {row.hoverAssets.map(a => (
                  <li key={a.id}>
                    <Typography variant='body2'>{a.name}</Typography>
                  </li>
                ))}
              </ul>
            </div>
          );
          return (
            <Tooltip enterDelay={400} title={row.hoverAssets.length ? hoverTitle : ''}>
              <span>{row.templateName}</span>
            </Tooltip>
          );
        },
      },
      {
        id: 'area',
        width: '16%',
        label: t(action_instances_dashboard_messages.column_area),
        get: row => translatedOrFallback(t, row.area.display_name_translation_key, row.area.display_name || row.area.name),
      },
      {
        id: 'subarea',
        width: '16%',
        label: t(action_instances_dashboard_messages.column_subarea),
        get: row => {
          if (!row.subArea) return '—';
          return translatedOrFallback(t, row.subArea.name_translation_key, row.subArea.name);
        },
      },
      {
        id: 'a',
        width: '14%',
        label: t(action_instances_dashboard_messages.column_a),
        get: row => (
          <Box
            className={classes.tableDrillLink}
            component='span'
            onClick={e => {
              e.stopPropagation();
              openDrillDown(row.templateId, 'a', row.templateName);
            }}
          >
            {row.aValidInstancesCount}
          </Box>
        ),
      },
      {
        id: 'k',
        width: '14%',
        label: t(action_instances_dashboard_messages.column_k),
        get: row => (
          <Box
            className={classes.tableDrillLink}
            component='span'
            onClick={e => {
              e.stopPropagation();
              openDrillDown(row.templateId, 'k', row.templateName);
            }}
          >
            {row.kCompletedValidInstancesCount}
          </Box>
        ),
      },
      {
        id: 't',
        width: '16%',
        label: t(action_instances_dashboard_messages.column_t),
        get: row => <span>{row.tActionsLastYearCount}</span>,
      },
    ],
    [classes.hoverList, classes.tableDrillLink, openDrillDown, t],
  );

  const showHelp = () => {
    showHelpDialog({
      title: t(action_instances_dashboard_messages.help_title),
      body: t(action_instances_dashboard_messages.help_body),
    });
  };

  const dialogTitle =
    drillDown?.column === 'a'
      ? `${t(action_instances_dashboard_messages.dialog_title_a)}: ${drillDown.templateName}`
      : `${t(action_instances_dashboard_messages.dialog_title_k)}: ${drillDown?.templateName ?? ''}`;

  const kpiTiles = useMemo(() => {
    if (!dashboardKpi) return [];
    return [
      {
        key: 'completion',
        title: formatCompletionRate(dashboardKpi.completionRate),
        description: t(action_instances_dashboard_messages.kpi_completion),
      },
      {
        key: 'sumA',
        title: String(dashboardKpi.sumAValidInstancesCount),
        description: t(action_instances_dashboard_messages.kpi_sum_a),
      },
      {
        key: 'sumK',
        title: String(dashboardKpi.sumKCompletedValidInstancesCount),
        description: t(action_instances_dashboard_messages.kpi_sum_k),
      },
    ];
  }, [dashboardKpi, t]);

  const handleResetFilters = () => setFilters({});

  return (
    <Container disableGutters={!upMd} maxWidth='lg'>
      <Jumbotron>
        <Typography component='h1' variant='h1'>
          {t(title_messages.action_instances_dashboard)}
        </Typography>
      </Jumbotron>
      <MobileGuttersContainer>
        <Box className={classes.helpBar}>
          <TextButton onClick={showHelp}>{t(action_instances_dashboard_messages.help_button)}</TextButton>
        </Box>
      </MobileGuttersContainer>
      <MobileGuttersContainer>
        <Grid className={classes.kpiGrid} container spacing={2}>
          {kpiTiles.map((tile, index) => (
            <Grid key={tile.key} item lg={4} md={4} sm={4} xs={12}>
              <div className={classes.kpiTileOuter}>
                <ColoredTile
                  centered
                  className={classes.kpiTile}
                  color={resolveKpiColor(theme, index)}
                  description={tile.description}
                  disableRipple
                  isLoading={isLoading && !dashboardKpi}
                  title={tile.title}
                />
              </div>
            </Grid>
          ))}
        </Grid>
      </MobileGuttersContainer>
      <MobileGuttersContainer>
        {isError && (
          <Box className={classes.errorBanner}>
            <Typography color='error' gutterBottom variant='body2'>
              {t(general_messages.api_error)}
            </Typography>
            <TextButton onClick={() => refetch()}>{t(general_messages.retry)}</TextButton>
          </Box>
        )}
        {!availableFilters && <Loader inner />}
        {availableFilters && isLoading && offset === 0 && !hasHydratedDashboardRef.current && <Loader inner />}
        {showDashboardTable && (
          <>
            <Table
              data={accumulatedRows}
              dataMapping={dataMapping}
              filtersService={{ availableFilters, filters, setFilters }}
              fixedHeader
              keyGenerator={row => String(row.templateId)}
              onSort={() => {}}
              refetching={isFetching && (!isLoading || hasHydratedDashboardRef.current)}
              sort={{ key: null, direction: null }}
            />
            {accumulatedRows.length === 0 && !isLoading && (
              <Box className={classes.emptyState}>
                <Typography gutterBottom variant='body2'>
                  {t(general_messages.no_data)}
                </Typography>
                <TextButton onClick={handleResetFilters}>{t(general_messages.clear_all)}</TextButton>
              </Box>
            )}
            {hasNextPage && (
              <FooterButton className='' loading={isFetching} onClick={loadNextPage}>
                {t(general_messages.show_more)}
              </FooterButton>
            )}
          </>
        )}
      </MobileGuttersContainer>
      <TemplatesPivotInstancesDialog
        baseQueryParams={filterParams}
        column={drillDown?.column ?? 'a'}
        onClose={closeDrillDown}
        open={drillDown != null}
        templateId={drillDown?.templateId ?? null}
        title={dialogTitle}
      />
    </Container>
  );
};

export default ActionInstancesDashboardPage;
